import { sanityClient } from "./sanityClient";
import { supabase } from "../../supabaseClient";
import { secureKnowledgeChecksEnabled } from "../learning/secureKnowledgeChecks";
import { attachLevelVideosToRuntimeLevel } from "../learning/levelVideoFlow";

export function normalizeQuestKey(qid) {
  if (!qid) return null;

  if (qid === "financial-foundations" || qid === "quest-1" || qid === "Q1") {
    return "1";
  }

  if (qid === "quest-2" || qid === "Q2") return "2";
  if (qid === "quest-3" || qid === "Q3") return "3";

  return String(qid);
}

export function normalizeLevelKey(levelKey) {
  if (!levelKey) return "";

  const s = String(levelKey).trim();

  if (/^L\d+$/i.test(s)) return s.toUpperCase();
  if (/^\d+$/.test(s)) return `L${s}`;

  const m = s.match(/(\d+)/);

  return m ? `L${m[1]}` : s.toUpperCase();
}

export function cleanCanvasLabel(value = "", prefixes = []) {
  let cleaned = String(value || "").trim();

  prefixes.forEach((prefix) => {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const compact = prefix.replace(/\s+/g, "");
    const escapedCompact = compact.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    cleaned = cleaned
      .replace(new RegExp(`^${escaped}\\s*:?\\s*`, "i"), "")
      .replace(new RegExp(`^${escapedCompact}\\s*:?\\s*`, "i"), "");
  });

  return cleaned.trim();
}

export function slugifyStepLabel(value = "") {
  return cleanCanvasLabel(value, [
    "Page Name",
    "Name",
    "CMS Name",
    "Title",
    "Heading",
    "Question",
  ])
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function getStepSegment(step) {
  return slugifyStepLabel(
    step?.internalTitle ||
      step?.displayTitle ||
      step?.heading ||
      step?.title ||
      step?._type ||
      "step"
  );
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

function byKnowledgeCheckNumber(a, b) {
  return (
    toNumber(a?.knowledgeCheckNumber, 999) -
    toNumber(b?.knowledgeCheckNumber, 999)
  );
}

function byActionQuestionNumber(a, b) {
  return (
    toNumber(a?.actionQuestionNumber, 999) -
    toNumber(b?.actionQuestionNumber, 999)
  );
}

function makeKey(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildFeedbackMap(draftSteps = []) {
  const map = {};

  draftSteps.forEach((step) => {
    const number = step?.knowledgeCheckNumber;

    if (!number) return;

    if (!map[number]) {
      map[number] = {};
    }

    if (step._type === "correctFeedbackDraftStep") {
      map[number].correctFeedbackText = step.feedbackText || "";
    }

    if (step._type === "incorrectFeedbackDraftStep") {
      map[number].incorrectFeedbackText = step.feedbackText || "";
      map[number].incorrectSupportText = step.supportText || "";
    }
  });

  return map;
}

function transformScenarioSelectStep(step, feedbackMap) {
  const number = step?.knowledgeCheckNumber;
  const feedback = feedbackMap[number] || {};

  const prompt = [step?.scenarioParagraphOne, step?.questionText]
    .filter(Boolean)
    .join("\n\n");

  return {
    ...step,

    _type: "scenarioSelectStep",
    _key: step?._key || `scenario-select-${number}`,

    internalTitle: `knowledge-check-${number}`,
    displayTitle: `Knowledge Check ${number}`,

    knowledgeCheckNumber: number,

contextImage: step?.contextImage || null,
characterImage: step?.contextImage || null,
characterImageSrc: step?.contextImage?.asset?.url || "",
characterImageAlt: "",

scenarioText: step?.scenarioParagraphOne || "",
scenarioParagraphOne: step?.scenarioParagraphOne || "",

questionText: step?.questionText || "",
prompt,
    options: Array.isArray(step?.answers)
      ? step.answers.map((answer, index) => {
          const isCorrect = secureKnowledgeChecksEnabled ? false : answer?.isCorrect === true;

          return {
            ...answer,
            _key: answer?._key || `answer-${index + 1}`,
            id: answer?._key || index + 1,
            title: answer?.answerText || "",
            label: answer?.answerText || "",
            answerStatus: isCorrect ? "Correct" : "Incorrect",
            isCorrect,
            correct: isCorrect,
          };
        })
      : [],

    correctFeedbackText: feedback.correctFeedbackText || "",
    incorrectFeedbackText: feedback.incorrectFeedbackText || "",
    incorrectSupportText: feedback.incorrectSupportText || "",

    ctaNextLabel: "NEXT",
    showTitle: true,
  };
}

function transformScenarioDragStep(step, feedbackMap) {
  const number = step?.knowledgeCheckNumber;
  const feedback = feedbackMap[number] || {};

  return {
    ...step,

    _type: "scenarioDragStep",
    _key: step?._key || `scenario-drag-${number}`,

    internalTitle: `knowledge-check-${number}`,
    displayTitle: `Knowledge Check ${number}`,

    knowledgeCheckNumber: number,

contextImage: step?.contextImage || null,
characterImage: step?.contextImage || null,
characterImageSrc: step?.contextImage?.asset?.url || "",
characterImageAlt: "",

scenarioText: "",
prompt: step?.questionText || "",
questionText: step?.questionText || "",

    items: Array.isArray(step?.dragAnswers)
      ? step.dragAnswers.map((answer, index) => ({
          ...answer,
          _key: answer?._key || `drag-answer-${index + 1}`,
          id: answer?._key || `drag-answer-${index + 1}`,
          text: answer?.answerText || "",
          matchKey: secureKnowledgeChecksEnabled ? "" : (answer?.correctBoxKey || ""),
        }))
      : [],

    targets: Array.isArray(step?.dropBoxes)
      ? step.dropBoxes.map((box, index) => ({
          ...box,
          _key: box?._key || `drop-box-${index + 1}`,
          id: box?.boxKey || `drop-box-${index + 1}`,
          text: box?.boxLabel || "",
          matchKey: box?.boxKey || "",
        }))
      : [],

    correctFeedbackText: feedback.correctFeedbackText || "",
    incorrectFeedbackText: feedback.incorrectFeedbackText || "",
    incorrectSupportText: feedback.incorrectSupportText || "",

    nextLabel: "NEXT",
    showTitle: true,
  };
}

function transformPracticalActionStep(step, index) {
  return {
    ...step,
    _type: "practicalActionDraftStep",
    _key: step?._key || `practical-action-${index + 1}`,
    internalTitle: `practical-action-${index + 1}`,
    displayTitle: step?.actionTitle || "Practical Action",
  };
}

function transformActionQuestionStep(step, index) {
  const number = step?.actionQuestionNumber || index + 1;

  return {
    ...step,

    _type: "actionQuestionDraftStep",
    _key: step?._key || `action-question-${number}`,

    internalTitle: `action-question-${number}`,
    displayTitle: `Action Question ${number}`,

    actionQuestionNumber: number,
    questionText: step?.questionText || "",

    options: Array.isArray(step?.options)
      ? step.options.map((option, optionIndex) => ({
          ...option,
          _key: option?._key || `action-option-${optionIndex + 1}`,
          id: option?._key || `action-option-${optionIndex + 1}`,
          title: option?.optionText || "",
          label: option?.optionText || "",
          text: option?.optionText || "",
        }))
      : [],
  };
}

function transformPracticalCompleteStep(step, index) {
  return {
    ...step,
    _type: "practicalActionCompleteDraftStep",
    _key: step?._key || `practical-complete-${index + 1}`,
    internalTitle: "practical-complete",
    displayTitle: step?.heading || "Practical Action Complete",
  };
}

function transformLevelDraftToRuntimeLevel(levelDraft) {
  if (!levelDraft?._id) return null;

  const draftSteps = Array.isArray(levelDraft.draftSteps)
    ? levelDraft.draftSteps
    : [];

  const feedbackMap = buildFeedbackMap(draftSteps);

  const scenarioSteps = draftSteps
    .filter((step) =>
      ["scenarioSelectDraftStep", "scenarioDragDraftStep"].includes(step?._type)
    )
    .sort(byKnowledgeCheckNumber)
    .map((step) => {
      const runtimeStep = step._type === "scenarioDragDraftStep"
        ? transformScenarioDragStep(step, feedbackMap)
        : transformScenarioSelectStep(step, feedbackMap);
      return {
        ...runtimeStep,
        curriculumGeneration: levelDraft.curriculumGeneration || null,
assessmentVersion: levelDraft.assessmentVersion ?? null,
      };
    });

  // Only Sanity-authored action pages count. Do not synthesize fallback
  // questions: a level without an authored action must not earn A-XP.
  const practicalActionSteps = draftSteps
    .filter((step) => step?._type === "practicalActionDraftStep")
    .map(transformPracticalActionStep);

  const actionQuestionSteps = draftSteps
    .filter((step) => step?._type === "actionQuestionDraftStep")
    .sort(byActionQuestionNumber)
    .map(transformActionQuestionStep);

  const practicalCompleteSteps = draftSteps
    .filter((step) => step?._type === "practicalActionCompleteDraftStep")
    .map(transformPracticalCompleteStep);

  // Sanity's array _key is the stable question identity for Supabase.
  // A generated display fallback key is not safe for verified scoring.
  const authoredQuestions = draftSteps.filter(
    (step) => step?._type === "actionQuestionDraftStep"
  );
  const requiredQuestionKeys = authoredQuestions
    .map((step) => String(step?._key || "").trim());
  const keysAreValid =
    requiredQuestionKeys.length > 0 &&
    requiredQuestionKeys.every(Boolean) &&
    new Set(requiredQuestionKeys).size === requiredQuestionKeys.length;
  const hasPracticalAction =
    practicalActionSteps.length > 0 && actionQuestionSteps.length > 0;

  // This is descriptive UI metadata only. Trusted server-side Sanity
  // synchronization must register the authoritative question list before
  // submit_my_learning_action_answer can issue a verified award.
  const practicalAction = {
    hasPracticalAction,
    actionQuestionCount: actionQuestionSteps.length,
    canSyncForScoring: hasPracticalAction && keysAreValid,
    requiredQuestionKeys: keysAreValid ? requiredQuestionKeys : [],
    sanityDocumentId: levelDraft._id,
    assessmentVersion: levelDraft.assessmentVersion || null,
  };

  const steps = [
    {
  _type: "welcomeStep",
  _key: "overview",

  internalTitle: "overview",
  displayTitle: levelDraft.title || "Level Overview",

  heading: levelDraft.title || "Level Overview",
  introText: levelDraft.levelOverviewText || "",

  levelHeading: levelDraft.title || "Level Overview",
  levelOverviewText: levelDraft.levelOverviewText || "",

  estimatedTime: "",
  xpLabel: `${levelDraft.xpMax || 150} XP`,
  overviewImageSrc: "",

  startLabel: "START",
},

    ...scenarioSteps,

    /*
     * Summary video will be inserted here later from Supabase.
     */

    ...practicalActionSteps,
    ...actionQuestionSteps,
    ...practicalCompleteSteps,

    {
      _type: "resultStep",
      _key: "result",
      internalTitle: "result",
      displayTitle: "Result",
    },
  ];

  return {
    _id: levelDraft._id,
    _type: "levelDraftRuntime",

    title: levelDraft.title || "",
    levelName: levelDraft.title || "",
    levelTitle: levelDraft.title || "",

    levelKey: levelDraft.levelKey || "",
levelOrder: levelDraft.levelOrder || null,
status: levelDraft.status || "draft",

// Preserve secure assessment metadata for Knowledge Check verification.
curriculumGeneration: levelDraft.curriculumGeneration || null,
assessmentVersion: levelDraft.assessmentVersion ?? null,

xpMax: levelDraft.xpMax || 150,

    sourceDocumentType: "levelDraft",
    sourceLevelDraftId: levelDraft._id,
    practicalAction,

    steps,
  };
}

async function fetchLevelDraftByLevelKey({ levelKey }) {
  const normalizedLevelKey = normalizeLevelKey(levelKey);

  if (secureKnowledgeChecksEnabled) {
    const isTestDataset =
  process.env.REACT_APP_SANITY_DATASET === "lesson-redesign-test";

const functionName = isTestDataset
  ? "get-sanity-lesson-test"
  : "get-sanity-lesson";

const { data, error } = await supabase.functions.invoke(functionName, {
  body: { levelKey: normalizedLevelKey },
});
    if (error) throw error;
    if (!data?.level) throw new Error("Secure lesson is unavailable or awaiting synchronization");
    return attachLevelVideosToRuntimeLevel(transformLevelDraftToRuntimeLevel(data.level));
  }

  const query = `
    *[
      _type == "levelDraft" &&
      defined(levelKey)
    ] | order(levelOrder asc) {
      _id,
      _type,
      title,
      levelKey,
      levelOrder,
      levelOverviewText,
      status,
      curriculumGeneration,
      assessmentVersion,
      xpMax,

      draftSteps[]{
        ...,
        _type,
        _key,

        knowledgeCheckNumber,

        contextImage{
          asset->{url}
        },

        scenarioParagraphOne,
        questionText,

        answers[]{
          ...,
          _key,
          answerText,
          isCorrect
        },

        dropBoxes[]{
          ...,
          _key,
          boxKey,
          boxLabel
        },

        dragAnswers[]{
          ...,
          _key,
          answerText,
          correctBoxKey
        },

        feedbackText,
        supportText,

        actionTitle,
        shortExplanation,

        actionQuestionNumber,
        options[]{
          ...,
          _key,
          optionText
        },

        heading,
        textOne,
        textTwo
      }
    }
  `;

  const drafts = (await sanityClient.fetch(query)) || [];

  console.log("[sanityLevels] Raw levelDrafts:", {
    requestedLevelKey: normalizedLevelKey,
    count: drafts.length,
    levelKeys: drafts.map((draft) => draft.levelKey),
  });

  const draft = drafts.find(
    (item) => normalizeLevelKey(item?.levelKey) === normalizedLevelKey
  );

  console.log("[sanityLevels] Matched levelDraft:", {
    requestedLevelKey: normalizedLevelKey,
    found: !!draft,
    matchedLevelKey: draft?.levelKey,
    title: draft?.title,
    draftSteps: draft?.draftSteps?.length || 0,
  });

  const runtimeLevel = transformLevelDraftToRuntimeLevel(draft);

if (!runtimeLevel) {
  return null;
}

  return attachLevelVideosToRuntimeLevel(runtimeLevel);
}

async function fetchLegacyLevel({ questId, subquestId, levelKey }) {
  const normalizedQuestId = normalizeQuestKey(questId);
  const normalizedLevelKey = normalizeLevelKey(levelKey);

  const query = `
    *[
      _type == "level" &&
      legacyQuestId == $questId &&
      legacySubquestId == $subquestId &&
      legacyLevelKey == $levelKey
    ][0]{
      _id,
      title,
      slug,
      xpMax,
      maxQuestions,
      legacyQuestId,
      legacySubquestId,
      legacyLevelKey,

      steps[]{
        ...,
        _type,
        _key,

        internalTitle,
        displayTitle,


        learningOutcomes,
        duration,
        xpMax,

        paragraphs,
        storyParagraphs,
        speechText,
        content,

        prompt,
        answersText,
        pairsText,

        nextLabel,
        submitLabel,
        ctaNextLabel,
        showTitle,

        illustration{
          asset->{url}
        },

        scenarioStep[]{
          ...,
          _type,
          _key,
          internalTitle,
          displayTitle,
          prompt,
          answersText,
          pairsText,
          ctaNextLabel,
          nextLabel,
          submitLabel,
          showTitle,

          options[]{
            ...,
            title,
            answerStatus,
            isCorrect,
            correct,
            icon{
              asset->{url}
            }
          },

          pairs[]{
            ...,
            dragText,
            dropText,
            matchKey
          },

          items[]{
            ...
          },

          targets[]{
            ...,
            icon{
              asset->{url}
            }
          }
        },

        options[]{
          ...,
          title,
          answerStatus,
          isCorrect,
          correct,
          icon{
            asset->{url}
          }
        },

        pairs[]{
          ...,
          dragText,
          dropText,
          matchKey
        },

        items[]{
          ...
        },

        targets[]{
          ...,
          icon{
            asset->{url}
          }
        }
      }
    }
  `;

  return sanityClient.fetch(query, {
    questId: String(normalizedQuestId ?? ""),
    subquestId: String(subquestId ?? ""),
    levelKey: String(normalizedLevelKey ?? ""),
  });
}

export async function fetchSanityLevelByLegacy({
  questId,
  subquestId,
  levelKey,
}) {
  /*
   * First try the new redesign Level Draft by levelKey.
   * Example: L1, L2, L3.
   */
  const draftLevel = await fetchLevelDraftByLevelKey({
    levelKey,
  });

  if (draftLevel) {
    return draftLevel;
  }
  if (secureKnowledgeChecksEnabled) {
    throw new Error("Secure lesson unavailable; legacy fallback disabled.");
  }

  /*
   * If no Level Draft exists, keep old production Sanity level support.
   */
  return fetchLegacyLevel({
    questId,
    subquestId,
    levelKey,
  });
}

export function getCurrentSegmentFromWildcard(wildcard) {
  const cleaned = String(wildcard || "").replace(/^\/+/, "").trim();
  return cleaned || "welcome";
}